import { sanitizeHtml, companyName as companyNameOf, safeHref, textRows } from "@1-now/booking-core";
import { buildContactRows, companyContactRows } from "utils/contactRows";
import ContactTypeIcon from "designs/Common/ContactTypeIcon";
import { FooterLink } from "data/types";
import React from "react";
import { Link } from "react-router-dom";
import Logo from "shared/Logo/Logo";
import { useData } from "data/data-provider";

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: FooterLink[];
}

const Footer: React.FC = () => {
  const {
    socialItems: rawSocialItems,
    address: rawAddress,
    contactItems: rawContactItems,
    footerItems: rawFooterItems,
    footer,
    companyDetails,
  } = useData();
  // The operator's rows, read the package's way: text only — a description
  // that arrives as an object no longer takes the whole site down — and
  // contact values shown without a stored scheme and linked when bare.
  const socialItems = textRows(rawSocialItems);
  // One address: the FMS keypoints when there are any, else the company's own.
  const addressKeypoints = textRows(rawAddress).filter((a: any) => a.description.trim());
  const companyAddress = String(companyDetails?.address ?? "").trim();
  const address =
    addressKeypoints.length > 0
      ? addressKeypoints
      : companyAddress
      ? [{ id: "company-address", description: companyAddress }]
      : [];
  // The operator's contact rows, else the company's own phone and email, as the Contact page
  // does (1NOW-11109). With neither, the Contact Us column is not drawn at all.
  const ownContactRows = buildContactRows(rawContactItems);
  const contactItems = ownContactRows.length > 0 ? ownContactRows : companyContactRows(companyDetails);
  const footerItems = textRows(rawFooterItems);

  const currentYear = new Date().getFullYear();
  const companyName: string = companyNameOf(companyDetails);

  // 1NOW-10372: legal links are not FMS-driven — every tenant site has to
  // expose a privacy policy and terms URL (SMS carriers require one before
  // approving a campaign), so this bar renders unconditionally, including on
  // sites whose footer has no content configured at all.
  const legalLinks = (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
      <Link
        to="/privacy-policy"
        className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm"
      >
        Privacy Policy
      </Link>
      <Link
        to="/terms-and-conditions"
        className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm"
      >
        Terms &amp; Conditions
      </Link>
      {/* 1NOW-11157 */}
      <Link
        to="/track-booking"
        className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm"
      >
        Track my booking
      </Link>
    </div>
  );

  const copyright = companyName ? (
    <p className="text-text-color text-xs sm:text-sm">
      © {currentYear} {companyName}. All rights reserved.
    </p>
  ) : null;

  // Columns layout: copyright left, legal links right — the two links on their
  // own read as unanchored under a left-aligned grid. Stacks centered below sm.
  const legalBarSplit = (
    <div className="container mt-10 border-t border-secondary-100 pt-5">
      <div className="flex flex-col-reverse items-center gap-3 sm:flex-row sm:justify-between">
        {copyright}
        <div className="sm:ml-auto">{legalLinks}</div>
      </div>
    </div>
  );

  // Centered layout (and the no-content footer): stay centered with the rest.
  const legalBarCentered = (
    <div className="container mt-8 border-t border-secondary-100 pt-5">
      <div className="flex flex-col items-center gap-3">
        {legalLinks}
        {copyright}
      </div>
    </div>
  );

  if (
    (!socialItems || socialItems.length === 0) &&
    (!address || address.length === 0) &&
    (!contactItems || contactItems.length === 0) &&
    (!footerItems || footerItems.length === 0)
  ) {
    return (
      <div className="nc-Footer relative py-6 border-t border-secondary-100 bg-bg-color">
        <div className="container flex flex-col items-center gap-3">
          {legalLinks}
          {copyright}
        </div>
      </div>
    );
  }

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "columns"  → 4-col grid (default, preserves the previous render)
  //   "centered" → centered single column: logo + info + nav row + contact +
  //                socials, all centered. Reuses Design A's own footer classes
  //                so the centered variant matches Design A styling.
  const layoutVariant: "columns" | "centered" =
    footer?.layout_variant === "centered" ? "centered" : "columns";

  if (layoutVariant === "centered") {
    return (
      <div className="nc-Footer relative py-8 border-t border-secondary-100 bg-bg-color">
        <div className="container flex flex-col items-center text-center gap-8">
          <div>
            <Logo />
          </div>

          {/* Footer tagline — the footer Section's own `description`, set in
              the FMS "Footer tagline" field. Rich text (Quill HTML). */}
          {footer?.description && (
            <p
              className="max-w-xl text-text-color text-xs sm:text-sm leading-relaxed [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
            />
          )}

          {/* Info lines — footerItems without a heading read as plain info
              lines under the brand (same convention Design B uses). */}
          {footerItems.some((item: any) => !item.heading) && (
            <div className="max-w-xl text-sm space-y-3">
              {footerItems
                .filter((item: any) => !item.heading)
                .map((item: any, index: any) => (
                  <p
                    key={item.id || index}
                    className="text-text-color break-words text-xs sm:text-sm"
                  >
                    {item.description}
                  </p>
                ))}
            </div>
          )}

          {/* Centered horizontal nav row — footerItems WITH a heading. */}
          {footerItems.some((item: any) => item.heading) && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {footerItems
                .filter((item: any) => item.heading)
                .map((item: any, index: any) => (
                  <a
                    key={item.id || index}
                    className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm"
                    href={safeHref(item.description)}
                  >
                    {item.heading}
                  </a>
                ))}
            </div>
          )}

          {/* Centered contact row. */}
          {contactItems.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {contactItems.map((item, index) => {
                const text = item.label ? `${item.label}: ${item.display}` : item.display;
                return (
                  <div key={item.id || index} className="flex items-start gap-2 text-text-color">
                    <span className="mt-1.5 flex h-4 w-4 shrink-0 items-center sm:h-5">
                      <ContactTypeIcon type={item.icon} />
                    </span>
                    {item.href ? (
                      <a
                        className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm break-words"
                        href={item.href}
                      >
                        {text}
                      </a>
                    ) : (
                      <p className="py-1.5 text-text-color break-words text-xs sm:text-sm">{text}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Centered address row(s). */}
          {address.length > 0 && (
            <div className="text-sm space-y-3">
              {address.map((item: any, index: any) => (
                <p
                  key={item.id || index}
                  className="text-text-color break-words text-xs sm:text-sm"
                >
                  {item.description}
                </p>
              ))}
            </div>
          )}

          {/* Centered social row — same look as the columns layout. */}
          {socialItems.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {socialItems.map((item: any, index: any) => (
                <a
                  key={item.id || index}
                  href={safeHref(item.description)}
                  // 1NOW-1650: these leave the site. Opening in the same tab
                  // dropped renters out of a booking they were mid-way through.
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-bold text-sm hover:opacity-80 transition"
                >
                  {item.heading.toUpperCase()}
                </a>
              ))}
            </div>
          )}
        </div>

        {legalBarCentered}
      </div>
    );
  }

  return (
    <div className="nc-Footer relative py-8 border-t border-secondary-100 bg-bg-color">
      <div className="container grid grid-cols-1 gap-y-10 gap-x-4 sm:gap-x-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-x-8">
        <div className="col-span-1 md:col-span-2 lg:col-span-1">
          <div className="flex flex-col gap-5">
            <div>
              <Logo />
            </div>
            {/* Footer tagline — the footer Section's own `description`, set in
                the FMS "Footer tagline" field. Rich text (Quill HTML). */}
            {footer?.description && (
              <p
                className="text-text-color text-xs sm:text-sm leading-relaxed [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(footer.description) }}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              {socialItems.map((item: any, index: any) => (
                <a
                  key={item.id || index}
                  href={safeHref(item.description)}
                  // 1NOW-1650: these leave the site. Opening in the same tab
                  // dropped renters out of a booking they were mid-way through.
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-bold text-sm hover:opacity-80 transition"
                >
                  {item.heading.toUpperCase()}
                </a>
              ))}
            </div>
          </div>
        </div>
        {address.length > 0 && (
          <div className="text-sm">
            <h2 className="font-bold text-text-color mb-5">Address</h2>
            <div className="space-y-3">
              {address.map((item: any, index: any) => (
                <p key={item.id || index} className="text-text-color break-words text-xs sm:text-sm">
                  {item.description}
                </p>
              ))}
            </div>
          </div>
        )}
        {contactItems.length > 0 && (
        <div className="text-sm">
          <h2 className="font-bold text-text-color mb-5">Contact Us</h2>
          <div className="space-y-3">
            {contactItems.map((item, index) => {
              const text = item.label ? `${item.label}: ${item.display}` : item.display;
              return (
                <div key={item.id || index} className="flex items-start gap-2 text-text-color">
                  <span className="mt-1.5 flex h-4 w-4 shrink-0 items-center sm:h-5">
                    <ContactTypeIcon type={item.icon} />
                  </span>
                  {item.href ? (
                    <a
                      className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm break-words"
                      href={item.href}
                    >
                      {text}
                    </a>
                  ) : (
                    <p className="py-1.5 text-text-color break-words text-xs sm:text-sm">{text}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
        <div className="text-sm">
          <h2 className="font-bold text-text-color mb-5">Links</h2>
          <div className="space-y-3">
            {footerItems.map((item: any, index: any) => (
              <div key={item.id || index}>
                {item.heading ? (
                  <a
                    className="inline-block py-1.5 text-text-color hover:underline transition text-xs sm:text-sm"
                    href={safeHref(item.description)}
                  >
                    {item.heading}
                  </a>
                ) : (
                  <p className="text-text-color break-words text-xs sm:text-sm">
                    {item.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {legalBarSplit}
    </div>
  );
};

export default Footer;
